import * as React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { cn } from "../../utils/utils";
import {
    ChartConfig,
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
    ChartTooltipFormatter,
} from "../ui/chart";

export interface BarTrendSeries {
    /** Chave da série no objeto de dados (= dataKey). Deve existir no `config`. */
    key: string;
    /** Textura da barra: pontos ou listras (com contorno da cor). Default: sólida. */
    pattern?: "dots" | "stripes";
}

interface BarTrendChartProps {
    data: Array<Record<string, unknown>>;
    /** Rótulos + cores das séries. A cor vira `--color-<key>`. */
    config: ChartConfig;
    /** Chave do eixo X. */
    xKey: string;
    series: BarTrendSeries[];
    /** Exibe o eixo Y (default: false). */
    showYAxis?: boolean;
    /** Exibe a legenda das séries (default: false). Útil com múltiplas séries. */
    showLegend?: boolean;
    xTickFormatter?: (value: string) => string;
    labelFormatter?: (value: string) => string;
    tooltipFormatter?: ChartTooltipFormatter;
    className?: string;
}

/** Gráfico de barras padronizado sobre `ui/chart`. Cores via `config`, nunca hardcoded. */
export function BarTrendChart({
    data,
    config,
    xKey,
    series,
    showYAxis = false,
    showLegend = false,
    xTickFormatter,
    labelFormatter,
    tooltipFormatter,
    className,
}: BarTrendChartProps) {
    return (
        <ChartContainer config={config} className={cn("aspect-auto h-full w-full", className)}>
            <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                    {series
                        .filter((s) => s.pattern)
                        .map((s) => (
                            <pattern
                                key={s.key}
                                id={`barptn-${s.key}`}
                                patternUnits="userSpaceOnUse"
                                width={6}
                                height={6}
                            >
                                <rect width="6" height="6" fill={`var(--color-${s.key})`} />
                                {s.pattern === "stripes" ? (
                                    <path d="M0,6 L6,0" stroke="hsl(var(--background))" strokeWidth="1" opacity="0.3" />
                                ) : (
                                    <circle cx="3" cy="3" r="1" fill="hsl(var(--background))" opacity="0.4" />
                                )}
                            </pattern>
                        ))}
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
                <XAxis
                    dataKey={xKey}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={8}
                    tickFormatter={xTickFormatter}
                />
                {showYAxis && (
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} width={32} />
                )}
                <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted-foreground) / 0.1)" }}
                    content={
                        <ChartTooltipContent
                            indicator="dot"
                            labelFormatter={labelFormatter}
                            formatter={tooltipFormatter}
                        />
                    }
                />
                {showLegend && <ChartLegend content={<ChartLegendContent />} />}
                {series.map((s) => (
                    <Bar
                        key={s.key}
                        dataKey={s.key}
                        fill={s.pattern ? `url(#barptn-${s.key})` : `var(--color-${s.key})`}
                        stroke={s.pattern ? `var(--color-${s.key})` : undefined}
                        strokeWidth={s.pattern ? 1.5 : 0}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={48}
                    />
                ))}
            </BarChart>
        </ChartContainer>
    );
}
